import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/Toast';
import { apiErrorMessage } from '@/components/analysis/reportAnalysisUi';
import { isManualReportAnalysisProfileId } from '@/components/analysis/analysisAccountMode';
import { useAuth } from '@/contexts/AuthContext';
import { adsApi, api } from '@/api/client';
import {
  analysisMonthCount,
  defaultAnalysisMonthWindow,
  normalizeAnalysisConfig,
  normalizeInventoryCell,
  type ReportAnalysisInventoryCell,
  type ReportAnalysisJob,
} from '@/types/reportAnalysis';

const MAX_MONTHS = 4;
const CONFIG_SAVE_DEBOUNCE_MS = 400;

function invalidateShareOfVoice(queryClient: QueryClient, profileId: string) {
  void queryClient.invalidateQueries({ queryKey: ['sov-summary', profileId] });
  void queryClient.invalidateQueries({ queryKey: ['sov-detail', profileId] });
}

function configPayload(state: {
  brandNames: string[];
  competitorNames: string[];
  accountName: string;
}): { brandNames: string[]; competitorNames: string[]; accountName: string } {
  return {
    brandNames: state.brandNames,
    competitorNames: state.competitorNames,
    accountName: state.accountName,
  };
}

export function useReportAnalysis(
  profileId: string,
  options?: { skipInventory?: boolean; skipConfig?: boolean }
) {
  const skipConfig = Boolean(options?.skipConfig);
  const persistAccountName = isManualReportAnalysisProfileId(profileId);
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const defaults = useMemo(() => defaultAnalysisMonthWindow(), []);
  const [startMonth, setStartMonth] = useState(defaults.startMonth);
  const [endMonth, setEndMonth] = useState(defaults.endMonth);
  const [brandNames, setBrandNames] = useState<string[]>([]);
  const [competitorNames, setCompetitorNames] = useState<string[]>([]);
  const [accountName, setAccountNameState] = useState('');
  const [configHydrated, setConfigHydrated] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [zipName, setZipName] = useState<string | null>(null);
  const persistEnabledRef = useRef(false);
  const pendingSaveRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const appliedConfigProfileRef = useRef('');
  const stateRef = useRef({ brandNames, competitorNames, accountName });
  stateRef.current = { brandNames, competitorNames, accountName };
  const loadErrorToastRef = useRef('');

  const monthsOk = analysisMonthCount(startMonth, endMonth);
  const windowError =
    monthsOk <= 0
      ? 'Choose a valid start and end month.'
      : monthsOk > MAX_MONTHS
        ? `Pick at most ${MAX_MONTHS} months.`
        : null;

  const inventoryQuery = useQuery({
    queryKey: ['report-analysis-inventory', profileId, startMonth, endMonth],
    queryFn: async () => {
      const res = await adsApi.getReportAnalysisInventory(profileId, { startMonth, endMonth });
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load S3 coverage');
      return {
        ...res.data,
        cells: (res.data.cells ?? []).map((cell) =>
          normalizeInventoryCell(cell as unknown as Record<string, unknown>)
        ),
        missing: (res.data.missing ?? []).map((cell) =>
          normalizeInventoryCell(cell as unknown as Record<string, unknown>)
        ),
      };
    },
    enabled: Boolean(profileId) && !windowError && !options?.skipInventory,
  });

  const jobsQuery = useQuery({
    queryKey: ['report-analysis-jobs', profileId],
    queryFn: async () => {
      const res = await adsApi.listReportAnalysisJobs(profileId);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load jobs');
      return res.data;
    },
    enabled: Boolean(profileId),
    refetchInterval: (query) => {
      const rows = query.state.data ?? [];
      return rows.some((job) => job.status === 'queued' || job.status === 'running') ? 4000 : false;
    },
  });

  const configQuery = useQuery({
    queryKey: ['report-analysis-config', profileId],
    queryFn: async () => {
      const res = await adsApi.getReportAnalysisConfig(profileId);
      if (!res.success) throw new Error(res.message || 'Failed to load analysis settings');
      return normalizeAnalysisConfig(
        (res.data ?? {}) as unknown as Record<string, unknown>,
        profileId
      );
    },
    enabled: Boolean(profileId) && !skipConfig,
  });

  const saveConfig = useCallback(
    async (pid: string, payload: ReturnType<typeof configPayload>) => {
      const res = await adsApi.putReportAnalysisConfig(pid, payload);
      if (!res.success) throw new Error(res.message || 'Failed to save analysis settings');
      const saved = normalizeAnalysisConfig(
        (res.data ?? payload) as unknown as Record<string, unknown>,
        pid
      );
      queryClient.setQueryData(['report-analysis-config', pid], saved);
      if (isManualReportAnalysisProfileId(pid)) {
        void queryClient.invalidateQueries({ queryKey: ['manual-analysis-accounts'] });
      }
      invalidateShareOfVoice(queryClient, pid);
      return saved;
    },
    [queryClient]
  );

  const flushConfigSave = useCallback(
    async (pid: string) => {
      if (!pid || !persistEnabledRef.current || !pendingSaveRef.current) return;
      pendingSaveRef.current = false;
      const payload = configPayload(stateRef.current);
      try {
        await saveConfig(pid, payload);
      } catch (error) {
        pendingSaveRef.current = true;
        showToast(apiErrorMessage(error, 'Failed to save brand and competitor names'), 'error');
      }
    },
    [saveConfig, showToast]
  );

  const scheduleConfigSave = useCallback(
    (pid: string) => {
      if (!pid || !persistEnabledRef.current) return;
      pendingSaveRef.current = true;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        void flushConfigSave(pid);
      }, CONFIG_SAVE_DEBOUNCE_MS);
    },
    [flushConfigSave]
  );

  useEffect(() => {
    persistEnabledRef.current = false;
    pendingSaveRef.current = false;
    appliedConfigProfileRef.current = '';
    setConfigHydrated(false);
    setBrandNames([]);
    setCompetitorNames([]);
    setAccountNameState('');
    stateRef.current = { brandNames: [], competitorNames: [], accountName: '' };

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      if (profileId && persistEnabledRef.current && pendingSaveRef.current) {
        const payload = configPayload(stateRef.current);
        pendingSaveRef.current = false;
        void adsApi
          .putReportAnalysisConfig(profileId, payload)
          .then((res) => {
            if (!res.success) throw new Error(res.message || 'Failed to save analysis settings');
            invalidateShareOfVoice(queryClient, profileId);
          })
          .catch((error) => {
            showToast(apiErrorMessage(error, 'Failed to save brand and competitor names'), 'error');
          });
      }
    };
  }, [profileId, queryClient, showToast]);

  useEffect(() => {
    if (!profileId || skipConfig) {
      persistEnabledRef.current = false;
      setConfigHydrated(true);
      return;
    }
    if (configQuery.isError) {
      if (loadErrorToastRef.current !== profileId) {
        loadErrorToastRef.current = profileId;
        showToast(apiErrorMessage(configQuery.error, 'Could not load saved brand and competitor names'), 'error');
      }
      appliedConfigProfileRef.current = profileId;
      persistEnabledRef.current = true;
      setConfigHydrated(true);
      return;
    }
    if (!configQuery.isSuccess || !configQuery.data) return;
    if (appliedConfigProfileRef.current === profileId) return;
    appliedConfigProfileRef.current = profileId;
    const next = configQuery.data;
    setBrandNames(next.brandNames);
    setCompetitorNames(next.competitorNames);
    setAccountNameState(next.accountName);
    stateRef.current = {
      brandNames: next.brandNames,
      competitorNames: next.competitorNames,
      accountName: next.accountName,
    };
    persistEnabledRef.current = true;
    pendingSaveRef.current = false;
    setConfigHydrated(true);
  }, [
    profileId,
    skipConfig,
    configQuery.isError,
    configQuery.isSuccess,
    configQuery.data,
    configQuery.error,
    showToast,
  ]);

  const setBrandNamesAndSave = useCallback(
    (next: string[]) => {
      setBrandNames(next);
      stateRef.current = { ...stateRef.current, brandNames: next };
      scheduleConfigSave(profileId);
    },
    [profileId, scheduleConfigSave]
  );

  const setCompetitorNamesAndSave = useCallback(
    (next: string[]) => {
      setCompetitorNames(next);
      stateRef.current = { ...stateRef.current, competitorNames: next };
      scheduleConfigSave(profileId);
    },
    [profileId, scheduleConfigSave]
  );

  const setAccountName = useCallback(
    (next: string) => {
      setAccountNameState(next);
      if (!persistAccountName) return;
      stateRef.current = { ...stateRef.current, accountName: next };
      scheduleConfigSave(profileId);
    },
    [persistAccountName, profileId, scheduleConfigSave]
  );

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['report-analysis-jobs', profileId] });
    void queryClient.invalidateQueries({ queryKey: ['report-analysis-inventory', profileId] });
  }, [profileId, queryClient]);

  const startMutation = useMutation({
    mutationFn: async () => {
      await flushConfigSave(profileId);
      const res = await adsApi.startReportAnalysis(profileId, {
        startMonth,
        endMonth,
        brandNames,
        competitorNames,
      });
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to start analysis');
      return res.data;
    },
    onSuccess: (job) => {
      const email = job.recipientEmail || user?.email || 'your email';
      showToast(`We’ll email Excel and PDF to ${email} when ready. This usually takes several minutes.`, 'success');
      invalidate();
    },
    onError: (error) => showToast(apiErrorMessage(error, 'Failed to start analysis'), 'error'),
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      await flushConfigSave(profileId);
      const res = await adsApi.uploadReportAnalysisZip(profileId, file, {
        startMonth,
        endMonth,
        brandNames: brandNames.join(','),
        competitorNames: competitorNames.join(','),
      });
      if (!res.success || !res.data) throw new Error(res.message || 'Upload failed');
      return res.data;
    },
    onSuccess: (job) => {
      const email = job.recipientEmail || user?.email || 'your email';
      showToast(`Zip stored. We’ll email Excel and PDF to ${email} when ready.`, 'success');
      setZipName(null);
      invalidate();
    },
    onError: (error) => showToast(apiErrorMessage(error, 'Zip upload failed'), 'error'),
  });

  const retryMutation = useMutation({
    mutationFn: async (jobId: string) => {
      const res = await adsApi.retryReportAnalysisEmail(profileId, jobId);
      if (!res.success) throw new Error(res.message || 'Retry failed');
      return res.data;
    },
    onSuccess: () => {
      showToast('Email retry queued', 'success');
      invalidate();
    },
    onError: (error) => showToast(apiErrorMessage(error, 'Email retry failed'), 'error'),
  });

  const download = useCallback(
    async (job: ReportAnalysisJob, format: 'xlsx' | 'pdf') => {
      try {
        const url = adsApi.downloadReportAnalysisUrl(profileId, job.id, format);
        const res = await api.get(url, { responseType: 'blob' });
        const blob = new Blob([res.data], {
          type: format === 'xlsx'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'application/pdf',
        });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `search-ads-diagnosis-${job.startMonth}-${job.endMonth}.${format}`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch {
        showToast(`Could not download ${format.toUpperCase()}`, 'error');
      }
    },
    [profileId, showToast]
  );

  const cells = inventoryQuery.data?.cells ?? [];
  const months = useMemo(() => [...new Set(cells.map((cell) => cell.month))], [cells]);
  const byMonth = useMemo(() => {
    const map = new Map<string, ReportAnalysisInventoryCell[]>();
    for (const cell of cells) {
      const list = map.get(cell.month) ?? [];
      list.push(cell);
      map.set(cell.month, list);
    }
    return map;
  }, [cells]);
  const missingCount = inventoryQuery.data?.missing?.length ?? 0;
  const jobs = jobsQuery.data ?? [];
  const busy = startMutation.isPending || uploadMutation.isPending;
  const configLoading = Boolean(profileId) && !skipConfig && (!configHydrated || configQuery.isLoading);

  return {
    startMonth,
    setStartMonth,
    endMonth,
    setEndMonth,
    brandNames,
    setBrandNames: setBrandNamesAndSave,
    competitorNames,
    setCompetitorNames: setCompetitorNamesAndSave,
    accountName,
    setAccountName,
    configLoading,
    windowError,
    inventoryQuery,
    jobsQuery,
    startMutation,
    uploadMutation,
    retryMutation,
    download,
    fileRef,
    zipName,
    setZipName,
    cells,
    months,
    byMonth,
    missingCount,
    jobs,
    busy,
  };
}
